import React from 'react';
import Avatar from '@mui/material/Avatar';
import { SxProps, Theme } from '@mui/material/styles';

// TODO: update avatar colors using pounceV2 theme tokens
const colors = [
  '#7268A6', // violet-400
  '#894195', // purple-400
  '#67B52C', // green-400
  '#B35D00', // yellow-700
  '#B81C77', // magenta-300
  '#09A084', // teal-500
  '#C55773', // pink-500
  '#E98702', // yellow-600
  '#169CA7', // cyan-500
];

function stringToColor(string): string {
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    /* eslint-disable no-bitwise */
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
    hash &= hash;
  }
  // convert the hash to an index in our colors array
  hash = ((hash % colors.length) + colors.length) % colors.length;
  return colors[hash];
}

type UserAvatarSize = 'small' | 'large';

function stringAvatarStyles(name: string, size): { sx: SxProps<Theme>; size: UserAvatarSize } {
  const commonStyles = {
    bgcolor: stringToColor(name),
    color: '#FFFFFF',
    fontWeight: 'bold',
  };

  let sizeStyles;
  switch (size) {
    case 'large': {
      sizeStyles = { width: 48, height: 48, fontSize: '20px' };
      break;
    }
    case 'small':
    default: {
      sizeStyles = { width: 16, height: 16, fontSize: '7px' };
    }
  }

  return {
    ...commonStyles,
    ...sizeStyles,
  };
}

const getInitials = (name = ''): string => {
  const parts = name.split(' ').filter(word => word && !word.startsWith('('));

  if (parts.length === 1) {
    return parts[0].length >= 2 ? parts[0].slice(0, 2) : parts[0].slice(0, 1);
  }

  if (parts.length === 2) {
    return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`;
  }

  if (parts.length > 2) {
    return `${parts[0].slice(0, 1)}${parts[parts.length - 1].slice(0, 1)}`;
  }

  return '';
};

type UserAvatarProps = {
  entityId: string;
  name?: string;
  size?: UserAvatarSize;
  sx?: SxProps<Theme>;
};

const UserAvatarV3 = ({
  name,
  entityId,
  size = 'small',
  sx,
}: UserAvatarProps): React.ReactElement<UserAvatarProps> => {
  const initials = getInitials(name);

  return (
    <Avatar
      alt={name}
      sx={[
        { textTransform: 'uppercase' },
        { ...stringAvatarStyles(entityId, size) },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {initials}
    </Avatar>
  );
};

export default React.memo(UserAvatarV3);
